import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { CalendarFrame, Container, Title } from './style';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { useState, useMemo } from 'react';
import { format, isSameDay, isSameHour, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useProfileMeetingsWithFallback } from '../../hooks/useProfileMeetingsWithFallBack';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Typography,
    Chip,
    Stack,
    Button,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import PersonIcon from '@mui/icons-material/Person';
import MicIcon from '@mui/icons-material/Mic';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamIcon from '@mui/icons-material/Videocam';
import LoadingContent from '../../components/LoadingContent';
import { getServerNow, useServerTime } from '../../hooks/useServerTime';

const Calendar = () => {
    useServerTime();
    const { user } = useAuth();
    const { data, loading, error } = useProfileMeetingsWithFallback();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const navigate = useNavigate();

    // Mapa de datas: 'has_purchased' se algum horario foi comprado, 'available' se tem horarios abertos
    const dateStatus = useMemo(() => {
        const map = {};
        data?.forEach(meeting => {
            meeting.meeting_schedule?.forEach(schedule => {
                if (schedule.datetime) {
                    const dateStr = new Date(schedule.datetime).toISOString().split('T')[0];
                    const isPurchased = schedule.purchased || (schedule.Order && (Array.isArray(schedule.Order) ? schedule.Order.length > 0 : Boolean(schedule.Order)));
                    if (!map[dateStr]) map[dateStr] = { hasEvent: false, hasPurchased: false };
                    map[dateStr].hasEvent = true;
                    if (isPurchased) map[dateStr].hasPurchased = true;
                }
            });
        });
        return map;
    }, [data]);

    // Encontros do dia selecionado
    const dayMeetings = useMemo(() => {
        if (!data || !selectedDate) return [];

        const result = [];
        data.forEach(meeting => {
            const matchingSchedules = (meeting.meeting_schedule || []).filter(s =>
                s.datetime && isSameDay(new Date(s.datetime), selectedDate)
            );
            if (matchingSchedules.length > 0) {
                result.push({
                    ...meeting,
                    daySchedules: matchingSchedules.sort((a, b) => new Date(a.datetime) - new Date(b.datetime)),
                });
            }
        });

        // Ordenar por primeiro horario do dia
        result.sort((a, b) => new Date(a.daySchedules[0].datetime) - new Date(b.daySchedules[0].datetime));
        return result;
    }, [data, selectedDate]);

    if (loading) return <LoadingContent />;

    return (
        <Container>
            <Title>Meus encontros</Title>

            {user?.profile && !user.profile.approved && (
                <Alert severity="warning" sx={{ width: '100%', borderRadius: '12px', mb: 1, '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                    Seu cadastro de anfitrião está em análise. Seus encontros só ficarão visíveis para outros usuários após a aprovação.
                </Alert>
            )}

            <CalendarFrame>
                <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    dateClick={info => setSelectedDate(info.date)}
                    contentHeight="auto"
                    locale={ptBrLocale}
                    dayCellContent={arg => {
                        const dataStr = arg.date.toISOString().split('T')[0];
                        const status = dateStatus[dataStr];
                        const isSelected = selectedDate && isSameDay(arg.date, selectedDate);

                        return (
                            <div className={`celula-dia ${isSelected ? 'dia-selecionado' : ''}`}>
                                <div className="numero-dia">{arg.dayNumberText}</div>
                                {status?.hasEvent && (
                                    <div
                                        className="evento-indicador"
                                        style={status.hasPurchased ? { backgroundColor: '#2e7d32' } : {}}
                                    />
                                )}
                            </div>
                        );
                    }}
                />
            </CalendarFrame>

            {/* Detalhes do dia selecionado */}
            <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600} color="#333" sx={{ mb: 2, textTransform: 'capitalize' }}>
                    {format(selectedDate, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                </Typography>

                {dayMeetings.length === 0 ? (
                    <Box sx={{
                        bgcolor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e8e8e8',
                        p: 3,
                        textAlign: 'center',
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            Nenhum encontro neste dia
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {dayMeetings.map((meeting, idx) => {
                            const isHost = meeting._role === 'host';

                            return (
                                <Box
                                    key={`${meeting.id}-${idx}`}
                                    sx={{
                                        bgcolor: 'white',
                                        borderRadius: '12px',
                                        border: '1px solid #e8e8e8',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* Barra lateral colorida + conteudo */}
                                    <Box sx={{ display: 'flex' }}>
                                        <Box sx={{
                                            width: 4,
                                            flexShrink: 0,
                                            bgcolor: isHost ? '#006a61' : '#1976d2',
                                        }} />
                                        <Box sx={{ flex: 1, p: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                                                        {meeting.title}
                                                    </Typography>
                                                    {!isHost && meeting.Profile?.User?.name && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            com {meeting.Profile.User.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Chip
                                                    icon={isHost
                                                        ? <MicIcon sx={{ fontSize: 14 }} />
                                                        : <PersonIcon sx={{ fontSize: 14 }} />
                                                    }
                                                    label={isHost ? 'Anfitriao' : 'Conviva'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: isHost ? '#e6f3f1' : '#e3f2fd',
                                                        color: isHost ? '#006a61' : '#1976d2',
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        ml: 1,
                                                        flexShrink: 0,
                                                        '& .MuiChip-icon': { color: 'inherit' },
                                                    }}
                                                />
                                            </Box>

                                            {/* Horarios */}
                                            <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                                                {meeting.daySchedules.map((s, i) => {
                                                    const time = new Date(s.datetime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                                                    const isPurchased = s.purchased || (s.Order && (Array.isArray(s.Order) ? s.Order.length > 0 : Boolean(s.Order)));
                                                    const buyer = Array.isArray(s.Order) ? s.Order[0]?.user : s.Order?.user;

                                                    return (
                                                        <Stack key={i} direction="row" spacing={1} alignItems="center">
                                                            <Chip
                                                                icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                                                label={time}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: '0.75rem',
                                                                    height: 26,
                                                                    '& .MuiChip-icon': { color: '#888' },
                                                                }}
                                                            />
                                                            {isHost && isPurchased && buyer && (
                                                                <Chip
                                                                    label={`Comprado por ${buyer.name}`}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#e8f5e9',
                                                                        color: '#2e7d32',
                                                                        fontWeight: 500,
                                                                        fontSize: '0.7rem',
                                                                        height: 24,
                                                                    }}
                                                                />
                                                            )}
                                                            {isHost && !isPurchased && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Disponível
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    );
                                                })}
                                            </Stack>

                                            {/* Acoes */}
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => {
                                                        if (isHost) {
                                                            navigate(`/meus-encontros/${meeting.id}/horarios`);
                                                        } else {
                                                            const orderId = meeting.daySchedules[0]?.Order?.id || meeting.daySchedules[0]?.ID;
                                                            if (orderId) navigate(`/encontros/${orderId}/detalhes`);
                                                        }
                                                    }}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        color: '#006a61',
                                                    }}
                                                >
                                                    Ver detalhes
                                                </Button>
                                                {(() => {
                                                    const now = getServerNow();
                                                    // Encontro finalizado nao oferece entrada, mesmo dentro da hora marcada
                                                    const liveSchedule = meeting.daySchedules.find(
                                                        s => !s.finished_at && isSameHour(now, new Date(s.datetime))
                                                    );
                                                    if (!liveSchedule) return null;
                                                    const orderId = isHost
                                                        ? (Array.isArray(liveSchedule.Order) ? liveSchedule.Order[0]?.id : liveSchedule.Order?.id)
                                                        : (Array.isArray(liveSchedule.Order) ? liveSchedule.Order[0]?.id : liveSchedule.Order?.id);
                                                    if (!orderId) return null;
                                                    return (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<VideocamIcon sx={{ fontSize: 14 }} />}
                                                            onClick={() => navigate(`/encontros/${orderId}/chamada`)}
                                                            sx={{
                                                                textTransform: 'none',
                                                                fontSize: '0.75rem',
                                                                bgcolor: '#2e7d32',
                                                                borderRadius: '6px',
                                                                '&:hover': { bgcolor: '#1b5e20' },
                                                            }}
                                                        >
                                                            Entrar
                                                        </Button>
                                                    );
                                                })()}
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Container>
    );
};

export default Calendar;
